import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

const INIT_FETCH_DELAY = 500;
const HEALTH_TIMEOUT = 5000;

function HealthDot({ title, responseTime, isCheckingHealth, checkBackendHealth, refreshInterval }) {
    const theme = useTheme();

    useEffect(() => {
        const timeout = setTimeout(checkBackendHealth, INIT_FETCH_DELAY);
        return () => clearTimeout(timeout);
    }, [checkBackendHealth]);

    useEffect(() => {
        if (isCheckingHealth) return;
        const interval = setInterval(checkBackendHealth, refreshInterval);
        return () => clearInterval(interval);
    }, [isCheckingHealth, checkBackendHealth, refreshInterval]);

    const getColor = () => {
        if (responseTime === undefined) return theme.palette.text.disabled; // never fetched yet
        if (responseTime === null) return theme.palette.error.main; // fetch failed
        if (responseTime < 1000) return theme.palette.events.autoFixed;
        if (responseTime < HEALTH_TIMEOUT) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    const tooltipText =
        responseTime !== undefined && responseTime !== null
            ? `${title}: ${responseTime.toFixed(0)} ms`
            : responseTime === null
            ? `${title}: no response`
            : `${title}: checking…`;

    const color = getColor();
    const isDegraded = responseTime !== undefined && (responseTime === null || responseTime >= 1000);

    return (
        <Tooltip title={tooltipText} arrow>
            <Box
                component='span'
                aria-label={tooltipText}
                data-testid={`HealthDot-${title}`}
                sx={{
                    display: 'inline-block',
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    backgroundColor: color,
                    mx: 0.5,
                    verticalAlign: 'middle',
                    cursor: 'default',
                    flexShrink: 0,
                    boxShadow: `0 0 6px ${color}55`,
                    transition: 'background-color 0.4s ease, box-shadow 0.4s ease',
                    '@keyframes healthPulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.55, transform: 'scale(1.4)' },
                        '100%': { opacity: 1, transform: 'scale(1)' },
                    },
                    animation: isDegraded ? 'healthPulse 1.5s ease-in-out infinite' : 'none',
                }}
            />
        </Tooltip>
    );
}

HealthDot.propTypes = {
    title: PropTypes.string.isRequired,
    responseTime: PropTypes.number,
    isCheckingHealth: PropTypes.bool,
    checkBackendHealth: PropTypes.func.isRequired,
    refreshInterval: PropTypes.number.isRequired,
};

export default HealthDot;
